import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline, FilterList, Search, MoreVert, Close, Save } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import Swal from "sweetalert2";
import dayjs from "dayjs";

import {
  RollingForecast as RollingForecastModel,
  RollingForecastDetailPayload,
  RollingForecastJenis,
  RollingForecastPayload,
  RollingForecastDetail,
  createRollingForecast,
  deleteRollingForecast,
  deleteRollingForecastDetail,
  createRollingForecastDetail,
  getRollingForecastDetails,
  getRollingForecasts,
  updateRollingForecast,
  updateRollingForecastDetail,
} from "../services/rollingForecastService.tsx";
import { getRap, RAPSearchParams } from "../services/rapService.tsx";

type Row = RollingForecastModel;

type FormState = {
  rfName: string;
  projectName: string;
  projectNumber: string;
  nilaiProject: string;
  jenis: RollingForecastJenis;
  detail: string;
  year: string;
};

type MonthPair = { rkap?: number; bobot?: number; forecast?: number };
type MonthlyBobot = { month: string; days: number; bobot: number; active: boolean };

const monthConfig = [
  { key: "january", label: "January" },
  { key: "february", label: "February" },
  { key: "march", label: "March" },
  { key: "april", label: "April" },
  { key: "may", label: "May" },
  { key: "june", label: "June" },
  { key: "july", label: "July" },
  { key: "august", label: "August" },
  { key: "september", label: "September" },
  { key: "october", label: "October" },
  { key: "november", label: "November" },
  { key: "december", label: "December" },
] as const;

type MonthKey = (typeof monthConfig)[number]["key"];

type DetailRow = {
  id: string;
  rollingForecastId?: string;
  projectName: string;
  projectNumber: string;
  nilaiProject: string;
  jenis: RollingForecastJenis;
  detailName: string;
  prediksiBulanStartDate: string;
  prediksiBulanEndDate: string;
  prediksiBulanDuration: number;
  months: Record<MonthKey, MonthPair>;
  rencanaBisnis?: number;
  realisasi?: number;
  isNew?: boolean;
};

const currency = (n: number) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

const formatIdrDisplay = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") return "";
  const numeric = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(numeric)) return "";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(numeric);
};

const normalizeIdrValue = (input?: string | number | null) => {
  if (input === null || input === undefined) return "";
  if (typeof input === "number") return Math.round(input).toString();
  const trimmed = input.replace(/Rp/gi, "").replace(/\s+/g, "");
  if (!trimmed) return "";
  const hasCommaDecimal = trimmed.includes(",") && trimmed.lastIndexOf(",") > trimmed.lastIndexOf(".");
  let sanitized = trimmed;
  if (hasCommaDecimal) {
    sanitized = sanitized.replace(/\./g, "").replace(/,/g, ".");
  } else {
    sanitized = sanitized.replace(/,/g, "");
  }
  const numeric = Number(sanitized);
  if (Number.isNaN(numeric)) return "";
  return Math.round(numeric).toString();
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("id-ID", { year: "numeric", month: "short", day: "numeric" }).format(date);
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatMonthLabel = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("id-ID", { month: "long" }).format(date);
};

const TOTAL_BOBOT = 100;

const formatBobot = (value: number) => value.toFixed(4);

const calculateMonthlyBobot = (
  startDate?: string,
  endDate?: string,
  totalBobot?: number,
  yearOverride?: number
): MonthlyBobot[] => {
  if (!startDate || !endDate) return [];
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return [];
  if (end.isBefore(start, "day")) return [];
  if (totalBobot === undefined || totalBobot === null || !Number.isFinite(totalBobot) || totalBobot <= 0) {
    return [];
  }

  const year = yearOverride || start.year();
  const totalDays = end.diff(start, "day") + 1;
  if (totalDays <= 0) return [];
  const bobotHarian = totalBobot / totalDays;

  const result: MonthlyBobot[] = monthConfig.map(({ label }, index) => {
    const monthNumber = index + 1;
    const monthStart = dayjs(`${year}-${String(monthNumber).padStart(2, "0")}-01`);
    const monthEnd = monthStart.endOf("month");
    const overlapStart = start.isAfter(monthStart, "day") ? start : monthStart;
    const overlapEnd = end.isBefore(monthEnd, "day") ? end : monthEnd;
    let days = 0;
    if (!overlapStart.isAfter(overlapEnd, "day")) {
      days = overlapEnd.diff(overlapStart, "day") + 1;
    }
    return {
      month: label,
      days,
      bobot: bobotHarian * days,
      active: days > 0,
    };
  });

  const totalCalculated = result.reduce((sum, item) => sum + item.bobot, 0);
  const diff = totalBobot - totalCalculated;
  if (Math.abs(diff) > 1e-9) {
    const lastActiveIndex = [...result].reverse().findIndex((item) => item.active);
    if (lastActiveIndex !== -1) {
      const index = result.length - 1 - lastActiveIndex;
      result[index] = { ...result[index], bobot: result[index].bobot + diff };
    }
  }

  return result;
};

const buildMonthlyBobotMaps = (
  startDate?: string,
  endDate?: string,
  totalBobot?: number,
  yearOverride?: number
) => {
  const monthly = calculateMonthlyBobot(startDate, endDate, totalBobot, yearOverride);
  if (!monthly.length) return null;
  const bobotByKey = {} as Record<MonthKey, number>;
  const activeByKey = {} as Record<MonthKey, boolean>;
  const daysByKey = {} as Record<MonthKey, number>;
  monthConfig.forEach(({ key }, index) => {
    const entry = monthly[index];
    bobotByKey[key] = entry?.bobot ?? 0;
    activeByKey[key] = entry?.active ?? false;
    daysByKey[key] = entry?.days ?? 0;
  });
  return { bobotByKey, activeByKey, daysByKey };
};

const applyMonthlyBobot = (
  months: Record<MonthKey, MonthPair>,
  startDate?: string,
  endDate?: string,
  totalBobot?: number,
  yearOverride?: number
) => {
  const meta = buildMonthlyBobotMaps(startDate, endDate, totalBobot, yearOverride);
  if (!meta) {
    return monthConfig.reduce((acc, { key }) => {
      const current = months[key] ?? {};
      acc[key] = { ...current, bobot: 0 };
      return acc;
    }, {} as Record<MonthKey, MonthPair>);
  }
  return monthConfig.reduce((acc, { key }) => {
    const current = months[key] ?? {};
    acc[key] = {
      ...current,
      bobot: meta.bobotByKey[key],
    };
    return acc;
  }, {} as Record<MonthKey, MonthPair>);
};

const getBobotDisplay = (value: number, active: boolean) => (active ? formatBobot(value) : "—");

const extractMissingFields = (error: any): string[] => {
  const missing = error?.response?.data?.missingFields;
  if (Array.isArray(missing)) return missing as string[];
  return [];
};

const setHeaderErrorsFromMissing = (missing: string[], setter: React.Dispatch<React.SetStateAction<{ [k: string]: boolean }>>) => {
  if (!missing.length) return;
  setter((prev) => ({
    ...prev,
    rfName: prev.rfName || missing.includes("rollingForecastName"),
    projectName: prev.projectName || missing.includes("projectName"),
  }));
};

const generateRowId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Derive header Start/End from detail dates
const getHeaderStartDate = (row?: Row) => {
  const dates = (row?.details || [])
    .map((d) => d.prediksiBulanStartDate)
    .filter(Boolean) as string[];
  if (dates.length === 0) return "";
  const min = dates.reduce((acc, curr) => (new Date(curr) < new Date(acc) ? curr : acc));
  return min;
};

const getHeaderEndDate = (row?: Row) => {
  const dates = (row?.details || [])
    .map((d) => d.prediksiBulanEndDate)
    .filter(Boolean) as string[];
  if (dates.length === 0) return "";
  const max = dates.reduce((acc, curr) => (new Date(curr) > new Date(acc) ? curr : acc));
  return max;
};

const createEmptyDetailRow = (meta?: Partial<FormState>): DetailRow => {
  const nilai =
    meta?.nilaiProject !== undefined && meta?.nilaiProject !== null ? String(meta.nilaiProject) : "";
  const months = monthConfig.reduce((acc, { key }) => {
    acc[key] = {};
    return acc;
  }, {} as Record<MonthKey, MonthPair>);

  return {
    id: generateRowId(),
    rollingForecastId: "",
    projectName: meta?.projectName ?? "",
    projectNumber: meta?.projectNumber ?? "",
    nilaiProject: nilai,
    jenis: (meta?.jenis as RollingForecastJenis) ?? "pendapatan",
    detailName: meta?.detail ?? "",
    prediksiBulanStartDate: "",
    prediksiBulanEndDate: "",
    prediksiBulanDuration: 0,
    months,
    rencanaBisnis: undefined,
    realisasi: undefined,
    isNew: false,
  };
};

const createInitialFormState = (year: number): FormState => ({
  rfName: "",
  projectName: "",
  projectNumber: "",
  nilaiProject: "",
  jenis: "pendapatan",
  detail: "",
  year: String(year),
});

  const calculateDuration = (start: string, end: string) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 0;
    const diff = endDate.getTime() - startDate.getTime();
    if (diff <= 0) return 0;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const monthList = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ] as const;

  const getMonthValue = (dateStr?: string) => {
    if (!dateStr) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    return m ? m[2] : '';
  };

  const buildDateFromMonth = (yearStr: string | undefined, month: string, isEnd?: boolean) => {
    const defaultYear = new Date().getFullYear();
    const y = Number(yearStr) || defaultYear;
    const mm = Number(month);
    const lastDay = new Date(y, mm, 0).getDate();
    const day = isEnd ? lastDay : 1;
    const mmStr = month.padStart(2, '0');
    const ddStr = String(day).padStart(2, '0');
    return `${y}-${mmStr}-${ddStr}`;
  };

const mapDetailRowToPayload = (row: DetailRow): RollingForecastDetailPayload => ({
  prediksiBulanStartDate: row.prediksiBulanStartDate,
  prediksiBulanEndDate: row.prediksiBulanEndDate,
  prediksiBulanDuration: row.prediksiBulanDuration,
  januaryRkap: row.months.january?.rkap ?? 0,
  januaryBobot: row.months.january?.bobot ?? 0,
  januaryForecast: row.months.january?.forecast ?? 0,
  februaryRkap: row.months.february?.rkap ?? 0,
  februaryBobot: row.months.february?.bobot ?? 0,
  februaryForecast: row.months.february?.forecast ?? 0,
  marchRkap: row.months.march?.rkap ?? 0,
  marchBobot: row.months.march?.bobot ?? 0,
  marchForecast: row.months.march?.forecast ?? 0,
  aprilRkap: row.months.april?.rkap ?? 0,
  aprilBobot: row.months.april?.bobot ?? 0,
  aprilForecast: row.months.april?.forecast ?? 0,
  mayRkap: row.months.may?.rkap ?? 0,
  mayBobot: row.months.may?.bobot ?? 0,
  mayForecast: row.months.may?.forecast ?? 0,
  juneRkap: row.months.june?.rkap ?? 0,
  juneBobot: row.months.june?.bobot ?? 0,
  juneForecast: row.months.june?.forecast ?? 0,
  julyRkap: row.months.july?.rkap ?? 0,
  julyBobot: row.months.july?.bobot ?? 0,
  julyForecast: row.months.july?.forecast ?? 0,
  augustRkap: row.months.august?.rkap ?? 0,
  augustBobot: row.months.august?.bobot ?? 0,
  augustForecast: row.months.august?.forecast ?? 0,
  septemberRkap: row.months.september?.rkap ?? 0,
  septemberBobot: row.months.september?.bobot ?? 0,
  septemberForecast: row.months.september?.forecast ?? 0,
  octoberRkap: row.months.october?.rkap ?? 0,
  octoberBobot: row.months.october?.bobot ?? 0,
  octoberForecast: row.months.october?.forecast ?? 0,
  novemberRkap: row.months.november?.rkap ?? 0,
  novemberBobot: row.months.november?.bobot ?? 0,
  novemberForecast: row.months.november?.forecast ?? 0,
  decemberRkap: row.months.december?.rkap ?? 0,
  decemberBobot: row.months.december?.bobot ?? 0,
  decemberForecast: row.months.december?.forecast ?? 0,
  rencanaBisnis: row.rencanaBisnis ?? 0,
  realisasi: row.realisasi ?? 0,
});

const mapDetailResponseToDetailRow = (detail: RollingForecastDetail, yearOverride?: number): DetailRow => {
  const months = monthConfig.reduce((acc, { key }) => {
    const rkapKey = `${key}Rkap` as keyof RollingForecastDetail;
    const bobotKey = `${key}Bobot` as keyof RollingForecastDetail;
    const forecastKey = `${key}Forecast` as keyof RollingForecastDetail;
    acc[key] = {
      rkap: Number(detail[rkapKey]) || 0,
      bobot: Number(detail[bobotKey]) || 0,
      forecast: Number(detail[forecastKey]) || 0,
    };
    return acc;
  }, {} as Record<MonthKey, MonthPair>);

  const monthsWithBobot = applyMonthlyBobot(
    months,
    detail.prediksiBulanStartDate,
    detail.prediksiBulanEndDate,
    TOTAL_BOBOT,
    yearOverride
  );

  return {
    id: detail.id,
    rollingForecastId: detail.rollingForecastId,
    projectName: "",
    projectNumber: "",
    nilaiProject: "",
    jenis: "",
    detailName: "",
    prediksiBulanStartDate: detail.prediksiBulanStartDate || "",
    prediksiBulanEndDate: detail.prediksiBulanEndDate || "",
    prediksiBulanDuration: detail.prediksiBulanDuration ?? 0,
    months: monthsWithBobot,
    rencanaBisnis: detail.rencanaBisnis ?? 0,
    realisasi: detail.realisasi ?? 0,
    isNew: false,
  };
};


const sumMonthValues = (row: DetailRow, field: keyof MonthPair) =>
  monthConfig.reduce((total, { key }) => total + (row.months[key]?.[field] ?? 0), 0);

const getErrorMessage = (error: unknown) => {
  if (!error) return "Terjadi kesalahan. Silakan coba lagi.";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  const maybe = (error as any)?.response?.data?.message;
  return maybe || "Terjadi kesalahan. Silakan coba lagi.";
};

const RollingForecast: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [showSearch, setShowSearch] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState<DetailRow[]>([]);
  const [activeForecast, setActiveForecast] = useState<Row | null>(null);
  const [headerEditing, setHeaderEditing] = useState(false);
  const [headerForm, setHeaderForm] = useState<FormState>(() => createInitialFormState(new Date().getFullYear()));
  const [detailFormOpen, setDetailFormOpen] = useState(false);
  const [detailForm, setDetailForm] = useState<DetailRow>(createEmptyDetailRow());
  const [detailFormErrors, setDetailFormErrors] = useState<Record<string, boolean>>({});
  const [detailSubmitLoading, setDetailSubmitLoading] = useState(false);
  const [editingDetailId, setEditingDetailId] = useState<string | null>(null);
  const [detailDeletingId, setDetailDeletingId] = useState<string | null>(null);
  const [selectedDetailId, setSelectedDetailId] = useState<string | null>(null);
  const [inlineEditing, setInlineEditing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState<FormState>(() => createInitialFormState(currentYear));
  const [errors, setErrors] = useState<{ [k: string]: boolean }>({});
  const [rfTable, setRfTable] = useState<DetailRow[]>([]);
  const [selectedDetailIds, setSelectedDetailIds] = useState<string[]>([]);
  const [rapOptions, setRapOptions] = useState<any[]>([]);
  const [rapOptionsLoading, setRapOptionsLoading] = useState(false);
  const [headerLocked, setHeaderLocked] = useState(false);
  const detailMonthlyMeta = useMemo(
    () =>
      buildMonthlyBobotMaps(
        detailForm.prediksiBulanStartDate,
        detailForm.prediksiBulanEndDate,
        TOTAL_BOBOT,
        Number(activeForecast?.asOfSelected || currentYear)
      ),
    [detailForm.prediksiBulanStartDate, detailForm.prediksiBulanEndDate, activeForecast?.asOfSelected, currentYear]
  );

  useEffect(() => {
    if (!detailMonthlyMeta) return;
    setDetailForm((prev) => {
      const nextMonths = applyMonthlyBobot(
        prev.months,
        prev.prediksiBulanStartDate,
        prev.prediksiBulanEndDate,
        TOTAL_BOBOT,
        Number(activeForecast?.asOfSelected || currentYear)
      );
      const unchanged = monthConfig.every(
        ({ key }) => (prev.months[key]?.bobot ?? 0) === (nextMonths[key]?.bobot ?? 0)
      );
      if (unchanged) return prev;
      return { ...prev, months: nextMonths };
    });
  }, [detailMonthlyMeta, activeForecast?.asOfSelected, currentYear]);

  const resetAlerts = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  useEffect(() => {
    setSelectedDetailIds((prev) => prev.filter((id) => rfTable.some((row) => row.id === id)));
  }, [rfTable]);

  useEffect(() => {
    if (!detailRows.length) {
      setSelectedDetailId(null);
      setDetailForm(createEmptyDetailRow());
      setEditingDetailId(null);
      setInlineEditing(false);
      return;
    }
    if (selectedDetailId) return;
    const first = detailRows[0];
    setSelectedDetailId(first.id);
    setDetailForm({ ...first });
    setEditingDetailId(first.isNew ? null : first.id);
    setInlineEditing(false);
  }, [detailRows, selectedDetailId]);

  const loadRollingForecasts = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const data = await getRollingForecasts();
      const sorted = [...data].sort((a, b) => {
        const updatedA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const updatedB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return updatedB - updatedA;
      });
      setRows(sorted);
      setSelected([]);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRollingForecasts();
  }, [loadRollingForecasts]);

  useEffect(() => {
    if (!openAdd) return;

    const fetchRapOptions = async () => {
      setRapOptionsLoading(true);
      try {
        const result = await getRap({
          limit: 1000,
          offset: 0,
          orderBy: "createdAt",
          order: "DESC",
        } as RAPSearchParams);
        const data = Array.isArray(result?.data) ? result.data : [];
        setRapOptions(data);
      } catch (error) {
        console.error("Failed to fetch RAP options:", error);
        setRapOptions([]);
      } finally {
        setRapOptionsLoading(false);
      }
    };

    fetchRapOptions();
  }, [openAdd]);

  const filtered = useMemo(() => {
    if (!keyword.trim()) return rows;
    const k = keyword.toLowerCase();
    return rows.filter((r) => {
      const searchable = [
        r.rollingForecastName ?? "",
        r.projectName ?? "",
        r.projectNumber ?? "",
        r.source ?? "",
        r.region ?? "",
        r.detail ?? "",
        r.projectType ?? "",
        r.projectPhase ?? "",
        r.jenis ?? "",
        String(r.rabValue ?? ""),
        String(r.rapValue ?? ""),
        String(r.estIncome ?? ""),
        String(r.estExpense ?? ""),
      ];
      return searchable.some((value) => value.toLowerCase().includes(k));
    });
  }, [rows, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paged = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const toggleSelect = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleSelectAll = (checked: boolean) => setSelected(checked ? paged.map((r) => r.id) : []);

  const handleChangeForm = (name: keyof FormState, value: string) => {
    setForm((prev) => ({
      ...prev,
      [name]: name === "nilaiProject" ? normalizeIdrValue(value) : value,
    }));
  };

  const validateForm = () => {
    const required: Array<keyof FormState> = ["rfName", "projectName", "nilaiProject"];
    const nextErrors: Record<string, boolean> = {};
    required.forEach((key) => {
      nextErrors[key] = !String(form[key]).trim();
    });
    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      setErrorMessage("Mohon lengkapi field wajib.");
      return false;
    }

    return true;
  };

  const resetFormState = () => {
    const fresh = createInitialFormState(currentYear);
    setForm(fresh);
    setErrors({});
    setRfTable([]);
    setSelectedDetailIds([]);
    setHeaderLocked(false);
  };

  const handleOpenAdd = () => {
    resetAlerts();
    resetFormState();
    setOpenAdd(true);
  };

  const handleCloseAddDialog = () => {
    if (submitLoading) return;
    setOpenAdd(false);
    setHeaderLocked(false);
  };

  const handleLockHeader = () => {
    resetAlerts();
    if (!validateForm()) return;
    setHeaderLocked(true);
    setSuccessMessage("Data header dikunci. Tambahkan detail sebelum menyimpan.");
  };

  const handleUnlockHeader = () => {
    resetAlerts();
    setHeaderLocked(false);
  };

  const handleDetailFieldChange = (
    id: string,
    key:
      | "prediksiBulanStartDate"
      | "prediksiBulanEndDate"
      | "prediksiBulanDuration"
      | "rencanaBisnis"
      | "realisasi"
      | "jenis"
      | "detailName",
    value: string
  ) => {
    setRfTable((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        if (key === "prediksiBulanStartDate" || key === "prediksiBulanEndDate") {
          const next = { ...row, [key]: value };
          next.prediksiBulanDuration = calculateDuration(next.prediksiBulanStartDate, next.prediksiBulanEndDate);
          next.months = applyMonthlyBobot(
            next.months,
            next.prediksiBulanStartDate,
            next.prediksiBulanEndDate,
            TOTAL_BOBOT,
            Number(form.year) || currentYear
          );
          return next;
        }
        if (key === "prediksiBulanDuration") {
          return { ...row, prediksiBulanDuration: Number(value) || 0 };
        }
        if (key === "rencanaBisnis" || key === "realisasi") {
          const numeric = value.trim() === "" ? undefined : Number(value.replace(/[^0-9]/g, "")) || 0;
          return { ...row, [key]: numeric };
        }
        if (key === "detailName" || key === "jenis") {
          return { ...row, [key]: value };
        }
        const numeric = Number(value.replace(/[^0-9]/g, "")) || 0;
        return { ...row, [key]: numeric };
      })
    );
  };

  const handleDetailMonthChange = (id: string, month: MonthKey, field: keyof MonthPair, value: string) => {
    const numeric =
      value.trim() === "" ? undefined : Number(value.replace(/[^0-9]/g, "")) || 0;
    setRfTable((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const monthEntry = row.months[month] ?? {};
        return {
          ...row,
          months: {
            ...row.months,
            [month]: {
              ...monthEntry,
              [field]: numeric,
            },
          },
        };
      })
    );
  };

  const handleAddDetailRow = () => {
    resetAlerts();

    if (!headerLocked) {
      setErrorMessage("Klik \"Save Rolling Forecast\" terlebih dahulu untuk mengunci data header.");
      return;
    }

    // Tambah baris detail baru dengan snapshot header saat ini
    setRfTable((prev) => [
      ...prev,
      createEmptyDetailRow({
        projectName: form.projectName,
        projectNumber: form.projectNumber,
        nilaiProject: form.nilaiProject,
        detail: form.detail,
        jenis: form.jenis,
      }),
    ]);
    Swal.fire({
      icon: "success",
      title: "Detail row ditambahkan",
      text: "Lengkapi kolom detail lalu tekan Save Details.",
      timer: 1800,
      showConfirmButton: false,
    });
  };

  const handleRemoveDetailRow = (id: string) => {
    setRfTable((prev) => prev.filter((row) => row.id !== id));
    setSelectedDetailIds((prev) => prev.filter((selectedId) => selectedId !== id));
  };

  const toggleDetailSelect = (id: string) =>
    setSelectedDetailIds((prev) => (prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id]));

  const toggleSelectAllDetails = (checked: boolean) =>
    setSelectedDetailIds(checked ? rfTable.map((row) => row.id) : []);

  const loadForecastDetails = useCallback(
    async (forecastId: string, forecastMeta?: Row) => {
      setDetailLoading(true);
      // Prefill from already-fetched details (if available in list response) so user sees all rows immediately
      if (forecastMeta?.details && forecastMeta.details.length > 0) {
        const prefilled = forecastMeta.details
          .map((detail) => mapDetailResponseToDetailRow(detail, forecastMeta.asOfSelected))
          .map((row) => ({
          ...row,
          projectName: forecastMeta.projectName ?? row.projectName,
          projectNumber: forecastMeta.projectNumber ?? row.projectNumber,
          nilaiProject:
            forecastMeta.nilaiProject !== undefined && forecastMeta.nilaiProject !== null
              ? String(forecastMeta.nilaiProject)
              : row.nilaiProject,
          jenis: forecastMeta.jenis ?? row.jenis ?? "",
          detailName: forecastMeta.detail ?? row.detailName,
        }));
        setDetailRows(prefilled);
      }
      try {
        const data = await getRollingForecastDetails(forecastId);
        const mapped = data
          .map((detail) => mapDetailResponseToDetailRow(detail, forecastMeta?.asOfSelected))
          .map((row) => ({
          ...row,
          projectName: forecastMeta?.projectName ?? row.projectName,
          projectNumber: forecastMeta?.projectNumber ?? row.projectNumber,
          nilaiProject:
            forecastMeta?.nilaiProject !== undefined && forecastMeta?.nilaiProject !== null
              ? String(forecastMeta.nilaiProject)
              : row.nilaiProject,
          jenis:
            forecastMeta?.jenis ??
            row.jenis ??
            "",
          detailName: forecastMeta?.detail ?? row.detailName,
        }));
        setDetailRows(mapped);
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  const handleOpenDetailDialog = (forecast: Row) => {
    resetAlerts();
    setActiveForecast(forecast);
    setDetailDialogOpen(true);
    loadForecastDetails(forecast.id, forecast);
    setSelectedDetailId(null);
    setInlineEditing(false);
    // seed header form from selected forecast
    setHeaderForm({
      rfName: forecast.rollingForecastName || "",
      projectName: forecast.projectName || "",
      projectNumber: forecast.projectNumber || "",
      nilaiProject: String(forecast.nilaiProject ?? ""),
      jenis: (forecast.jenis as RollingForecastJenis) || "pendapatan",
      detail: forecast.detail || "",
      year: String(forecast.asOfSelected ?? new Date().getFullYear()),
    });
    setHeaderEditing(false);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setActiveForecast(null);
    setDetailRows([]);
    setDetailFormOpen(false);
    setEditingDetailId(null);
    setDetailFormErrors({});
    setDetailForm(createEmptyDetailRow());
    setSelectedDetailId(null);
    setInlineEditing(false);
    setHeaderEditing(false);
  };

  const handleOpenDetailForm = (detail?: DetailRow) => {
    resetAlerts();
    setDetailFormErrors({});
    if (detail) {
      setEditingDetailId(detail.isNew ? null : detail.id);
      setDetailForm({ ...detail });
    } else {
      const base = createEmptyDetailRow();
      base.rollingForecastId = activeForecast?.id;
      base.isNew = true;
      setEditingDetailId(null);
      setDetailForm(base);
    }
    setDetailFormOpen(true);
  };

  const handleCloseDetailForm = () => {
    if (detailSubmitLoading) return;
    setDetailFormOpen(false);
    setEditingDetailId(null);
    setDetailFormErrors({});
    setDetailForm(createEmptyDetailRow());
  };

  const handleAddDetailInline = () => {
    if (!activeForecast) return;
    resetAlerts();
    const base = createEmptyDetailRow({
      projectName: activeForecast.projectName,
      projectNumber: activeForecast.projectNumber,
      nilaiProject: String(activeForecast.nilaiProject ?? ""),
      detail: activeForecast.detail,
      jenis: activeForecast.jenis,
    });
    base.rollingForecastId = activeForecast.id;
    base.isNew = true;
    setDetailRows((prev) => [...prev, base]);
    setSelectedDetailId(base.id);
    setDetailForm(base);
    setEditingDetailId(null);
    setInlineEditing(true);
    setDetailFormErrors({});
  };

  const handleDetailFormFieldChange = (
    key: "prediksiBulanStartDate" | "prediksiBulanEndDate" | "prediksiBulanDuration" | "rencanaBisnis" | "realisasi",
    value: string
  ) => {
    setDetailFormErrors((prev) => ({ ...prev, [key]: false }));
    setDetailForm((prev) => {
      if (key === "prediksiBulanStartDate" || key === "prediksiBulanEndDate") {
        const next = { ...prev, [key]: value };
        next.prediksiBulanDuration = calculateDuration(next.prediksiBulanStartDate, next.prediksiBulanEndDate);
        next.months = applyMonthlyBobot(
          next.months,
          next.prediksiBulanStartDate,
          next.prediksiBulanEndDate,
          TOTAL_BOBOT,
          Number(activeForecast?.asOfSelected || currentYear)
        );
        return next;
      }
      if (key === "prediksiBulanDuration") {
        return { ...prev, prediksiBulanDuration: Number(value) || 0 };
      }
      if (key === "rencanaBisnis" || key === "realisasi") {
        const numeric = value.trim() === "" ? undefined : Number(value.replace(/[^0-9]/g, "")) || 0;
        return { ...prev, [key]: numeric };
      }
      const numeric = Number(value.replace(/[^0-9]/g, "")) || 0;
      return { ...prev, [key]: numeric };
    });
  };

  const handleDetailFormMonthChange = (month: MonthKey, field: keyof MonthPair, value: string) => {
    const numeric =
      value.trim() === "" ? undefined : Number(value.replace(/[^0-9]/g, "")) || 0;
    setDetailForm((prev) => {
      const monthEntry = prev.months[month] ?? {};
      return {
        ...prev,
        months: {
          ...prev.months,
          [month]: {
            ...monthEntry,
            [field]: numeric,
          },
        },
      };
    });
  };

  const validateDetailForm = () => {
    const nextErrors: Record<string, boolean> = {
      prediksiBulanStartDate: !detailForm.prediksiBulanStartDate,
      prediksiBulanEndDate: !detailForm.prediksiBulanEndDate,
    };
    setDetailFormErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      setErrorMessage("Tanggal mulai dan selesai detail wajib diisi.");
      return false;
    }
    return true;
  };

  const handleSubmitDetailForm = async () => {
    if (!activeForecast) return;
    if (!validateDetailForm()) return;

    const payload = mapDetailRowToPayload(detailForm);
    setDetailSubmitLoading(true);
    resetAlerts();

    try {
      if (editingDetailId) {
        await updateRollingForecastDetail(activeForecast.id, editingDetailId, payload);
        setSuccessMessage("Detail rolling forecast berhasil diperbarui.");
      } else {
        await createRollingForecastDetail(activeForecast.id, payload);
        setSuccessMessage("Detail rolling forecast berhasil dibuat.");
      }
      setDetailFormOpen(false);
      await loadForecastDetails(activeForecast.id, activeForecast);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setDetailSubmitLoading(false);
      setDetailForm(createEmptyDetailRow());
      setEditingDetailId(null);
    }
  };

  const handleDeleteDetail = async (detail: DetailRow) => {
    if (!activeForecast) return;
    if (detail.isNew) {
      setDetailRows((prev) => prev.filter((row) => row.id !== detail.id));
      if (selectedDetailId === detail.id) {
        setSelectedDetailId(null);
        setDetailForm(createEmptyDetailRow());
        setEditingDetailId(null);
        setInlineEditing(false);
        setDetailFormErrors({});
      }
      return;
    }
    const confirmDelete = window.confirm("Hapus detail rolling forecast ini?");
    if (!confirmDelete) return;

    setDetailDeletingId(detail.id);
    resetAlerts();

    try {
      await deleteRollingForecastDetail(activeForecast.id, detail.id);
      setSuccessMessage("Detail rolling forecast berhasil dihapus.");
      await loadForecastDetails(activeForecast.id, activeForecast);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setDetailDeletingId(null);
    }
  };

  const handleSelectDetailRow = (detail: DetailRow) => {
    // toggle hide when clicking the same row
    if (selectedDetailId === detail.id) {
      setSelectedDetailId(null);
      setDetailForm(createEmptyDetailRow());
      setEditingDetailId(null);
      setInlineEditing(false);
      setDetailFormErrors({});
      return;
    }

    setSelectedDetailId(detail.id);
    setDetailForm({ ...detail });
    setEditingDetailId(detail.isNew ? null : detail.id);
    setInlineEditing(false);
    setDetailFormErrors({});
    resetAlerts();
  };

  const handleHeaderFieldChange = (name: keyof FormState, value: string) => {
    setHeaderForm((prev) => {
      if (name === "nilaiProject") {
        const numeric = value.replace(/[^0-9]/g, "");
        return { ...prev, [name]: numeric } as FormState;
      }
      return { ...prev, [name]: value } as FormState;
    });
  };

  const handleHeaderSave = async () => {
    if (!activeForecast) return;
    setSubmitLoading(true);
    resetAlerts();
    try {
      const payload: RollingForecastPayload = {
        rollingForecastName: headerForm.rfName.trim(),
        nilaiProject: Number(headerForm.nilaiProject || 0),
        projectName: headerForm.projectName.trim(),
        jenis: headerForm.jenis,
        projectNumber: headerForm.projectNumber.trim(),
        detail: headerForm.detail.trim(),
        asOfSelected: Number(headerForm.year) || new Date().getFullYear(),
        details: [],
      };
      const updated = await updateRollingForecast(activeForecast.id, payload);
      setActiveForecast(updated);
      setSuccessMessage("Header rolling forecast berhasil diperbarui.");
      setHeaderEditing(false);
      await loadRollingForecasts();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleInlineSave = async () => {
    if (!activeForecast) return;
    if (!validateDetailForm()) return;
    setDetailSubmitLoading(true);
    resetAlerts();
    try {
      const payload = mapDetailRowToPayload(detailForm);
      if (detailForm.isNew || !editingDetailId) {
        await createRollingForecastDetail(activeForecast.id, payload);
        setSuccessMessage("Detail rolling forecast berhasil dibuat.");
      } else {
        await updateRollingForecastDetail(activeForecast.id, editingDetailId, payload);
        setSuccessMessage("Detail rolling forecast berhasil diperbarui.");
      }
      setInlineEditing(false);
      await loadForecastDetails(activeForecast.id, activeForecast);
      setSelectedDetailId(null);
      setDetailForm(createEmptyDetailRow());
      setEditingDetailId(null);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setDetailSubmitLoading(false);
    }
  };

  // Step 2: Create Rolling Forecast Details (bulk create)
  const handleSaveDetails = async () => {
    resetAlerts();
    if (!headerLocked) {
      setErrorMessage('Klik "Save Rolling Forecast" terlebih dahulu untuk mengunci data header.');
      return;
    }
    if (!rfTable.length) {
      setErrorMessage("Tambahkan minimal satu detail sebelum menyimpan.");
      return;
    }

    if (!validateForm()) {
      return;
    }

    const invalid = rfTable.some((row) => !row.prediksiBulanStartDate || !row.prediksiBulanEndDate);
    if (invalid) {
      setErrorMessage("Lengkapi Start Date dan End Date pada setiap baris detail.");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload: RollingForecastPayload = {
        rollingForecastName: form.rfName.trim(),
        nilaiProject: parseFloat(form.nilaiProject || "0") || 0,
        projectName: form.projectName.trim(),
        jenis: form.jenis,
        projectNumber: form.projectNumber.trim(),
        detail: form.detail.trim(),
        asOfSelected: Number(form.year) || currentYear,
        details: [],
      };

      const created = await createRollingForecast(payload);
      await Promise.all(
        rfTable.map((row) => createRollingForecastDetail(created.id, mapDetailRowToPayload(row)))
      );

      setSuccessMessage("Rolling forecast dan detail berhasil disimpan.");
      await Swal.fire({
        icon: "success",
        title: "Berhasil",
        text: "Rolling forecast dan detail berhasil disimpan.",
        timer: 2000,
        showConfirmButton: false,
      });
      await loadRollingForecasts();
      setOpenAdd(false);
      setHeaderLocked(false);
    } catch (error: any) {
      const missingFields = extractMissingFields(error);
      if (missingFields.length) {
        setHeaderErrorsFromMissing(missingFields, setErrors);
        setErrorMessage(`Lengkapi field wajib: ${missingFields.join(", ")}`);
      } else {
        setErrorMessage(getErrorMessage(error));
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;
    const confirmDelete = window.confirm("Hapus rolling forecast terpilih?");
    if (!confirmDelete) return;

    setDeleteLoading(true);
    resetAlerts();

    try {
      await Promise.all(selected.map((id) => deleteRollingForecast(id)));
      setSuccessMessage("Rolling forecast berhasil dihapus.");
      await loadRollingForecasts();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        Rolling Forecast List
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Box
        className="toolbar"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography>Show</Typography>
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>

          <Button
            variant="text"
            startIcon={<Search />}
            onClick={() => setShowSearch((s) => !s)}
            sx={{ color: "#333" }}
          >
            Search
          </Button>
          {showSearch && (
            <TextField
              size="small"
              placeholder="Type to search..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              sx={{ minWidth: 280 }}
            />
          )}

          <Button
            variant="text"
            color="error"
            disabled={selected.length === 0 || deleteLoading}
            sx={{ ml: 0.5 }}
            onClick={handleDeleteSelected}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : undefined}
          >
            Delete
          </Button>
          <Button
            variant="text"
            startIcon={<FilterList />}
            onClick={() => setOpenFilter(true)}
            sx={{ color: "#333" }}
          >
            Filters
          </Button>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          color="success"
          sx={{ borderRadius: 2, textTransform: "none" }}
          onClick={handleOpenAdd}
        >
          Add New Rolling Forecast
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Details: List Of All Product
      </Typography>

      <Paper elevation={0} sx={{ border: "1px solid #eee" }}>
        <TableContainer>
          <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < paged.length}
                    checked={paged.length > 0 && selected.length === paged.length}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
                    <TableCell>Project Name</TableCell>
                    <TableCell>Kode Project</TableCell>
                    <TableCell>RAB Value</TableCell>
                    <TableCell>RAP Value</TableCell>
                    <TableCell>Estimasi Pendapatan</TableCell>
                    <TableCell>Estimasi Pengeluaran</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                paged.map((r) => (
                  <TableRow key={r.id} hover sx={{ "& td": { color: "text.primary" } }}>
                    <TableCell padding="checkbox">
                    <Checkbox checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700} sx={{ color: 'text.primary' }}>{r.projectName || "-"}</Typography>
                  </TableCell>
                  <TableCell>{r.projectNumber || "-"}</TableCell>
                  <TableCell>{currency(Number(r.rabValue ?? r.nilaiProject ?? 0) || 0)}</TableCell>
                  <TableCell>{currency(Number(r.rapValue ?? r.rabValue ?? 0) || 0)}</TableCell>
                  <TableCell>{currency(Number(r.estIncome ?? 0) || 0)}</TableCell>
                  <TableCell>{currency(Number(r.estExpense ?? 0) || 0)}</TableCell>
                  <TableCell>{formatDate(getHeaderStartDate(r))}</TableCell>
                  <TableCell>{formatDate(getHeaderEndDate(r))}</TableCell>
                  <TableCell>{formatDateTime(r.createdAt)}</TableCell>
                    <TableCell align="center">
                    <Tooltip title="Manage Details">
                      <IconButton onClick={() => handleOpenDetailDialog(r)}>
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  </TableRow>
                ))}
              {!loading && paged.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#777" }}>
                    No data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end" }}>
          <Pagination page={page} count={totalPages} onChange={(_, p) => setPage(p)} size="small" />
        </Box>
      </Paper>

      <Dialog open={openFilter} onClose={() => setOpenFilter(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField label="Rolling Forecast Name" fullWidth size="small" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Project Name" fullWidth size="small" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Jenis" fullWidth size="small" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="As Of" fullWidth size="small" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFilter(false)}>Close</Button>
          <Button variant="contained" onClick={() => setOpenFilter(false)}>
            Apply
          </Button>
      </DialogActions>
    </Dialog>

      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="lg"
        fullWidth
        sx={{ backdropFilter: "blur(4px)" }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold">
            Rolling Forecast Details
          </Typography>
          <IconButton onClick={handleCloseDetailDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {activeForecast && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e5e5e5', borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                A. Rolling Forecast
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Rolling Forecast Name"
                    fullWidth
                    size="small"
                    value={headerForm.rfName}
                    onChange={(e) => handleHeaderFieldChange('rfName', e.target.value)}
                    disabled={!headerEditing}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nilai Project (Rp)"
                    fullWidth
                    size="small"
                    value={formatIdrDisplay(headerForm.nilaiProject)}
                    onChange={(e) => handleHeaderFieldChange('nilaiProject', normalizeIdrValue(e.target.value))}
                    disabled={!headerEditing}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Project Name"
                    fullWidth
                    size="small"
                    value={headerForm.projectName}
                    onChange={(e) => handleHeaderFieldChange('projectName', e.target.value)}
                    disabled={!headerEditing}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Jenis"
                    select
                    fullWidth
                    size="small"
                    value={headerForm.jenis}
                    onChange={(e) => handleHeaderFieldChange('jenis', e.target.value)}
                    disabled={!headerEditing}
                  >
                    <MenuItem value="pendapatan">Pendapatan</MenuItem>
                    <MenuItem value="biaya">Biaya</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Project Number"
                    fullWidth
                    size="small"
                    value={headerForm.projectNumber}
                    onChange={(e) => handleHeaderFieldChange('projectNumber', e.target.value.replace(/[^0-9]/g, ''))}
                    disabled={!headerEditing}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Detail"
                    select
                    fullWidth
                    size="small"
                    value={headerForm.detail}
                    onChange={(e) => handleHeaderFieldChange('detail', e.target.value)}
                    disabled={!headerEditing}
                  >
                    <MenuItem value="EPC Diangsur">EPC Diangsur</MenuItem>
                    <MenuItem value="EPC Murni Tunai">EPC Murni Tunai</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Tahun"
                    placeholder="Contoh: 2025"
                    fullWidth
                    size="small"
                    value={headerForm.year}
                    onChange={(e) => handleHeaderFieldChange('year', e.target.value.replace(/[^0-9]/g, ''))}
                    disabled={!headerEditing}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Box>
                  {!headerEditing ? (
                    <Button variant="outlined" onClick={() => setHeaderEditing(true)}>Edit</Button>
                  ) : (
                    <Button variant="text" color="inherit" onClick={() => { setHeaderEditing(false); setHeaderForm({
                      rfName: activeForecast.rollingForecastName || '',
                      projectName: activeForecast.projectName || '',
                      projectNumber: activeForecast.projectNumber || '',
                      nilaiProject: String(activeForecast.nilaiProject ?? ''),
                      jenis: (activeForecast.jenis as RollingForecastJenis) || 'pendapatan',
                      detail: activeForecast.detail || '',
                      year: String(activeForecast.asOfSelected ?? new Date().getFullYear()),
                    }); }}>Cancel</Button>
                  )}
                </Box>
                <Box>
                  {headerEditing && (
                    <Button variant="contained" color="primary" onClick={handleHeaderSave} disabled={submitLoading}>
                      {submitLoading ? 'Saving...' : 'Save Header'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAddDetailInline}>
              Add Detail
            </Button>
          </Box>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2}>No</TableCell>
                    <TableCell rowSpan={2}>Project Name</TableCell>
                    <TableCell rowSpan={2}>Project Number</TableCell>
                    <TableCell rowSpan={2}>Nilai Project</TableCell>
                    <TableCell rowSpan={2}>Jenis</TableCell>
                    <TableCell rowSpan={2}>Detail</TableCell>
                    <TableCell align="center" colSpan={2}>
                      Prediksi (BULAN)
                    </TableCell>
                    {monthConfig.map((m) => (
                      <TableCell key={`${m.key}-head`} align="center" colSpan={3}>
                        {m.label}
                      </TableCell>
                    ))}
                    <TableCell rowSpan={2}>Rencana Bisnis</TableCell>
                    <TableCell rowSpan={2}>Realisasi</TableCell>
                    <TableCell rowSpan={2} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    {monthConfig.map((m) => (
                      <React.Fragment key={`${m.key}-sub`}>
                        <TableCell>RKAP</TableCell>
                        <TableCell>Bobot</TableCell>
                        <TableCell>Forecast</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailLoading && (
                    <TableRow>
                      <TableCell colSpan={47} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!detailLoading &&
                    detailRows.map((detail, idx) => {
                      const bobotMeta = buildMonthlyBobotMaps(
                        detail.prediksiBulanStartDate,
                        detail.prediksiBulanEndDate,
                        TOTAL_BOBOT,
                        Number(activeForecast?.asOfSelected || currentYear)
                      );
                      const rkapTotal = sumMonthValues(detail, "rkap");
                      const forecastTotal = sumMonthValues(detail, "forecast");
                      return (
                        <TableRow key={detail.id} hover selected={selectedDetailId===detail.id} onClick={() => handleSelectDetailRow(detail)} sx={{ cursor: 'pointer', "& td": { color: "text.primary" } }}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Typography fontWeight={700} sx={{ color: 'text.primary' }}>{detail.projectName || '-'}</Typography>
                          </TableCell>
                          <TableCell>{detail.projectNumber || '-'}</TableCell>
                          <TableCell>{currency(Number(detail.nilaiProject || 0))}</TableCell>
                          <TableCell>{detail.jenis || '-'}</TableCell>
                          <TableCell>{detail.detailName || '-'}</TableCell>
                          <TableCell>{formatMonthLabel(detail.prediksiBulanStartDate)}</TableCell>
                          <TableCell>{formatMonthLabel(detail.prediksiBulanEndDate)}</TableCell>
                          {monthConfig.map((m) => (
                            <React.Fragment key={`${detail.id}-${m.key}`}>
                              <TableCell>{detail.months[m.key]?.rkap ?? 0}</TableCell>
                              <TableCell sx={{ color: bobotMeta?.activeByKey[m.key] ? "text.primary" : "text.disabled" }}>
                                {getBobotDisplay(detail.months[m.key]?.bobot ?? 0, bobotMeta?.activeByKey[m.key] ?? false)}
                              </TableCell>
                              <TableCell>{detail.months[m.key]?.forecast ?? 0}</TableCell>
                            </React.Fragment>
                          ))}
                          <TableCell>{detail.rencanaBisnis ?? "-"}</TableCell>
                          <TableCell>{detail.realisasi ?? "-"}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDetail(detail)}
                              disabled={detailDeletingId === detail.id}
                            >
                              {detailDeletingId === detail.id ? (
                                <CircularProgress size={14} />
                              ) : (
                                <DeleteOutline fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {!detailLoading && detailRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={47} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        Belum ada detail.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Inline View/Edit Panel */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              {selectedDetailId ? 'Detail Preview / Edit' : 'Pilih baris detail untuk melihat data'}
            </Typography>
            {!selectedDetailId && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Belum ada detail yang dipilih. Tambahkan atau pilih baris di tabel.
                </Typography>
                <Button variant="contained" size="small" startIcon={<Add />} onClick={handleAddDetailInline}>
                  Add Detail
                </Button>
              </Box>
            )}
            {selectedDetailId && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Start Date"
                      size="small"
                      fullWidth
                      value={getMonthValue(detailForm.prediksiBulanStartDate)}
                      onChange={(e) =>
                        inlineEditing &&
                        handleDetailFormFieldChange(
                          'prediksiBulanStartDate',
                          buildDateFromMonth(String(activeForecast?.asOfSelected || currentYear), e.target.value, false)
                        )
                      }
                      disabled={!inlineEditing}
                    >
                      {monthList.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="End Date"
                      size="small"
                      fullWidth
                      value={getMonthValue(detailForm.prediksiBulanEndDate)}
                      onChange={(e) =>
                        inlineEditing &&
                        handleDetailFormFieldChange(
                          'prediksiBulanEndDate',
                          buildDateFromMonth(String(activeForecast?.asOfSelected || currentYear), e.target.value, true)
                        )
                      }
                      disabled={!inlineEditing}
                    >
                      {monthList.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Duration (days)"
                      size="small"
                      type="number"
                      value={detailForm.prediksiBulanDuration}
                      onChange={(e) => inlineEditing && handleDetailFormFieldChange('prediksiBulanDuration', e.target.value)}
                      inputProps={{ min: 0 }}
                      fullWidth
                      disabled={!inlineEditing}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {monthConfig.map((m) => (
                    <Grid item xs={12} sm={6} md={4} key={`inline-${m.key}`}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {m.label}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <TextField
                          label="RKAP"
                          size="small"
                          value={detailForm.months[m.key]?.rkap ?? ''}
                          onChange={(e) => inlineEditing && handleDetailFormMonthChange(m.key, 'rkap', e.target.value)}
                          inputProps={{ inputMode: 'numeric' }}
                          fullWidth
                          disabled={!inlineEditing}
                        />
                        <TextField
                          label="Bobot"
                          size="small"
                          value={getBobotDisplay(detailForm.months[m.key]?.bobot ?? 0, detailMonthlyMeta?.activeByKey[m.key] ?? false)}
                          inputProps={{ inputMode: 'numeric' }}
                          fullWidth
                          InputProps={{ readOnly: true }}
                          disabled
                          sx={{ "& .MuiInputBase-input": { color: detailMonthlyMeta?.activeByKey[m.key] ? "text.primary" : "text.disabled" } }}
                        />
                        <TextField
                          label="Forecast"
                          size="small"
                          value={detailForm.months[m.key]?.forecast ?? ''}
                          onChange={(e) => inlineEditing && handleDetailFormMonthChange(m.key, 'forecast', e.target.value)}
                          inputProps={{ inputMode: 'numeric' }}
                          fullWidth
                          disabled={!inlineEditing}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Total Bobot: {formatBobot(sumMonthValues(detailForm, "bobot"))}
                  </Typography>
                </Box>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Rencana Bisnis"
                      size="small"
                      fullWidth
                      value={detailForm.rencanaBisnis ?? ''}
                      onChange={(e) => inlineEditing && handleDetailFormFieldChange('rencanaBisnis', e.target.value)}
                      inputProps={{ inputMode: 'numeric' }}
                      disabled={!inlineEditing}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Realisasi"
                      size="small"
                      fullWidth
                      value={detailForm.realisasi ?? ''}
                      onChange={(e) => inlineEditing && handleDetailFormFieldChange('realisasi', e.target.value)}
                      inputProps={{ inputMode: 'numeric' }}
                      disabled={!inlineEditing}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Box>
                    {!inlineEditing ? (
                      <Button variant="outlined" onClick={() => setInlineEditing(true)} disabled={!selectedDetailId}>
                        Edit
                      </Button>
                    ) : (
                      <Button variant="text" color="inherit" onClick={() => { setInlineEditing(false); handleSelectDetailRow(detailForm); }}>
                        Cancel
                      </Button>
                    )}
                  </Box>
                  <Box>
                    {inlineEditing && (
                      <Button variant="contained" color="primary" onClick={handleInlineSave} disabled={detailSubmitLoading}>
                        {detailSubmitLoading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>

        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button onClick={handleCloseDetailDialog} variant="outlined" color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailFormOpen} onClose={handleCloseDetailForm} maxWidth="md" fullWidth>
        <DialogTitle>{editingDetailId ? "Edit Rolling Forecast Detail" : "Add Rolling Forecast Detail"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Start Month"
                size="small"
                fullWidth
                value={getMonthValue(detailForm.prediksiBulanStartDate)}
                onChange={(e) => handleDetailFormFieldChange(
                  "prediksiBulanStartDate",
                  buildDateFromMonth(String(activeForecast?.asOfSelected || currentYear), e.target.value, false)
                )}
                error={!!detailFormErrors.prediksiBulanStartDate}
                helperText={detailFormErrors.prediksiBulanStartDate ? "Wajib diisi" : ""}
              >
                {monthList.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="End Month"
                size="small"
                fullWidth
                value={getMonthValue(detailForm.prediksiBulanEndDate)}
                onChange={(e) => handleDetailFormFieldChange(
                  "prediksiBulanEndDate",
                  buildDateFromMonth(String(activeForecast?.asOfSelected || currentYear), e.target.value, true)
                )}
                error={!!detailFormErrors.prediksiBulanEndDate}
                helperText={detailFormErrors.prediksiBulanEndDate ? "Wajib diisi" : ""}
              >
                {monthList.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Duration (days)"
                size="small"
                type="number"
                value={detailForm.prediksiBulanDuration}
                onChange={(e) => handleDetailFormFieldChange("prediksiBulanDuration", e.target.value)}
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {monthConfig.map((m) => (
              <Grid item xs={12} sm={6} md={4} key={m.key}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {m.label}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                  <TextField
                    label="RKAP"
                    size="small"
                    value={detailForm.months[m.key]?.rkap ?? ""}
                    onChange={(e) => handleDetailFormMonthChange(m.key, "rkap", e.target.value)}
                    inputProps={{ inputMode: "numeric" }}
                    fullWidth
                  />
                  <TextField
                    label="Bobot"
                    size="small"
                    value={getBobotDisplay(detailForm.months[m.key]?.bobot ?? 0, detailMonthlyMeta?.activeByKey[m.key] ?? false)}
                    inputProps={{ inputMode: "numeric" }}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    disabled
                    sx={{ "& .MuiInputBase-input": { color: detailMonthlyMeta?.activeByKey[m.key] ? "text.primary" : "text.disabled" } }}
                  />
                  <TextField
                    label="Forecast"
                    size="small"
                    value={detailForm.months[m.key]?.forecast ?? ""}
                    onChange={(e) => handleDetailFormMonthChange(m.key, "forecast", e.target.value)}
                    inputProps={{ inputMode: "numeric" }}
                    fullWidth
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Total Bobot: {formatBobot(sumMonthValues(detailForm, "bobot"))}
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Rencana Bisnis"
                size="small"
                fullWidth
                value={detailForm.rencanaBisnis ?? ""}
                onChange={(e) => handleDetailFormFieldChange("rencanaBisnis", e.target.value)}
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Realisasi"
                size="small"
                fullWidth
                value={detailForm.realisasi ?? ""}
                onChange={(e) => handleDetailFormFieldChange("realisasi", e.target.value)}
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailForm} disabled={detailSubmitLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleSubmitDetailForm} disabled={detailSubmitLoading}>
            {detailSubmitLoading ? "Saving..." : "Save Details"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAdd} onClose={handleCloseAddDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          A. Rolling Forecast
          <IconButton size="small" onClick={handleCloseAddDialog} aria-label="close">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Rolling Forecast Name"
                placeholder="Masukkan nama rolling forecast"
                fullWidth
                value={form.rfName}
                onChange={(e) => handleChangeForm("rfName", e.target.value)}
                error={!!errors.rfName}
                helperText={errors.rfName ? "Field wajib diisi" : ""}
                disabled={headerLocked}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nilai Project (Rp)"
                placeholder=""
                fullWidth
                value={formatIdrDisplay(form.nilaiProject)}
                onChange={(e) => handleChangeForm("nilaiProject", normalizeIdrValue(e.target.value))}
                error={!!errors.nilaiProject}
                helperText={errors.nilaiProject ? "Field wajib diisi" : ""}
                InputProps={{ readOnly: true }}
                disabled={headerLocked}
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "#f4f5f7",
                    cursor: "not-allowed",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={rapOptions}
                loading={rapOptionsLoading}
                getOptionLabel={(option: any) =>
                  option?.project?.projectName || option?.projectName || ""
                }
                disabled={headerLocked}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={
                  rapOptions.find(
                    (opt) =>
                      (opt?.project?.projectName || opt?.projectName || "") === form.projectName
                  ) || null
                }
                onChange={(_, newValue: any) => {
                  if (!newValue) {
                    handleChangeForm("projectName", "");
                    handleChangeForm("projectNumber", "");
                    handleChangeForm("nilaiProject", "");
                    return;
                  }

                  const project = newValue.project || {};
                  const projectName = project.projectName || newValue.projectName || "";
                  handleChangeForm("projectName", projectName);

                  const projectRef =
                    project.projectReferenceId || project.projectId || newValue.projectId || "";
                  if (projectRef) {
                    handleChangeForm("projectNumber", projectRef);
                  }

                  const nilaiSource =
                    newValue.totalPrice ??
                    newValue.nilaiKontrak ??
                    project.totalPrice ??
                    project.nilaiKontrak ??
                    "";
                  if (nilaiSource !== undefined && nilaiSource !== null && nilaiSource !== "") {
                    handleChangeForm("nilaiProject", normalizeIdrValue(nilaiSource));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project Name"
                    placeholder="Pilih project dari RAP"
                    error={!!errors.projectName}
                    helperText={errors.projectName ? "Field wajib diisi" : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Jenis"
                select
                fullWidth
                value={form.jenis}
                onChange={(e) => handleChangeForm("jenis", e.target.value)}
                disabled={headerLocked}
              >
                <MenuItem value="pendapatan">Pendapatan</MenuItem>
                <MenuItem value="biaya">Biaya</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Project Number"
                placeholder=""
                fullWidth
                value={form.projectNumber}
                onChange={(e) => handleChangeForm("projectNumber", e.target.value)}
                InputProps={{ readOnly: true }}
                disabled={headerLocked}
                sx={{
                  "& .MuiInputBase-input": {
                    backgroundColor: "#f4f5f7",
                    cursor: "not-allowed",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Detail"
                placeholder="Pilih detail"
                select
                fullWidth
                value={form.detail}
                onChange={(e) => handleChangeForm("detail", e.target.value)}
                disabled={headerLocked}
              >
                <MenuItem value="EPC Diangsur">EPC Diangsur</MenuItem>
                <MenuItem value="EPC Murni Tunai">EPC Murni Tunai</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Tahun"
                placeholder="Contoh: 2025"
                fullWidth
                value={form.year}
                onChange={(e) => handleChangeForm("year", e.target.value.replace(/[^0-9]/g, ""))}
                disabled={headerLocked}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              mt: 3,
              mb: 2,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {!headerLocked ? (
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleLockHeader}
                >
                  Save Rolling Forecast
                </Button>
              ) : (
                <>
                  <Typography variant="subtitle2" color="success.main">
                    Header terkunci. Tambahkan detail di tabel di bawah.
                  </Typography>
                  <Button variant="text" onClick={handleUnlockHeader}>
                    Edit Header
                  </Button>
                </>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {headerLocked
                ? "Setelah header dikunci, Anda dapat menambahkan detail dan menekan \"Save Details\"."
                : "Lengkapi header lalu klik \"Save Rolling Forecast\" untuk mengunci data sebelum menambah detail."}
            </Typography>
          </Box>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Rolling Forecast Details
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
            <Tooltip title={!headerLocked ? 'Simpan header terlebih dahulu' : ''}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddDetailRow}
                  disabled={!headerLocked}
                >
                  Add Detail Row
                </Button>
              </span>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              Semua data akan dikirim saat menekan "Save Details" di bagian bawah.
            </Typography>
          </Box>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" rowSpan={2}>
                      <Checkbox
                        checked={rfTable.length > 0 && selectedDetailIds.length === rfTable.length}
                        indeterminate={
                          selectedDetailIds.length > 0 && selectedDetailIds.length < rfTable.length
                        }
                        onChange={(e) => toggleSelectAllDetails(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell rowSpan={2}>No</TableCell>
                    <TableCell rowSpan={2}>Project Name</TableCell>
                    <TableCell rowSpan={2}>Project Number</TableCell>
                    <TableCell rowSpan={2}>Nilai Project</TableCell>
                    <TableCell rowSpan={2}>Jenis</TableCell>
                    <TableCell rowSpan={2}>Detail</TableCell>
                    <TableCell align="center" colSpan={2}>
                      Prediksi (BULAN)
                    </TableCell>
                    {monthConfig.map((m) => (
                      <TableCell key={m.key} align="center" colSpan={3}>
                        {m.label}
                      </TableCell>
                    ))}
                    <TableCell rowSpan={2}>Rencana Bisnis</TableCell>
                    <TableCell rowSpan={2}>Realisasi</TableCell>
                    <TableCell rowSpan={2} align="center">
                      Action
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    {monthConfig.map((m) => (
                      <React.Fragment key={`${m.key}-headers`}>
                        <TableCell>RKAP</TableCell>
                        <TableCell>Bobot</TableCell>
                        <TableCell>Forecast</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfTable.map((row, idx) => {
                    const bobotMeta = buildMonthlyBobotMaps(
                      row.prediksiBulanStartDate,
                      row.prediksiBulanEndDate,
                      TOTAL_BOBOT,
                      Number(form.year) || currentYear
                    );
                    return (
                    <TableRow key={row.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDetailIds.includes(row.id)}
                          onChange={() => toggleDetailSelect(row.id)}
                        />
                      </TableCell>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Typography fontWeight={600} sx={{ color: 'text.primary' }}>{row.projectName || "-"}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>{row.projectNumber || "-"}</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>{row.nilaiProject || "-"}</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={row.jenis || "pendapatan"}
                          onChange={(e) => handleDetailFieldChange(row.id, "jenis", e.target.value)}
                        >
                          <MenuItem value="pendapatan">Pendapatan</MenuItem>
                          <MenuItem value="biaya">Biaya</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={row.detailName || ""}
                          onChange={(e) => handleDetailFieldChange(row.id, "detailName", e.target.value)}
                        >
                          <MenuItem value="EPC Diangsur">EPC Diangsur</MenuItem>
                          <MenuItem value="EPC Murni Tunai">EPC Murni Tunai</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={getMonthValue(row.prediksiBulanStartDate)}
                          onChange={(e) =>
                            handleDetailFieldChange(
                              row.id,
                              "prediksiBulanStartDate",
                              buildDateFromMonth(form.year, e.target.value, false)
                            )
                          }
                        >
                          {monthList.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={getMonthValue(row.prediksiBulanEndDate)}
                          onChange={(e) =>
                            handleDetailFieldChange(
                              row.id,
                              "prediksiBulanEndDate",
                              buildDateFromMonth(form.year, e.target.value, true)
                            )
                          }
                        >
                          {monthList.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      {monthConfig.map((m) => (
                        <React.Fragment key={`${row.id}-${m.key}`}>
                          <TableCell sx={{ minWidth: 100 }}>
                            <TextField
                              size="small"
                              value={row.months[m.key]?.rkap ?? ""}
                              onChange={(e) => handleDetailMonthChange(row.id, m.key, "rkap", e.target.value)}
                              inputProps={{ inputMode: "numeric" }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            <TextField
                              size="small"
                              value={getBobotDisplay(row.months[m.key]?.bobot ?? 0, bobotMeta?.activeByKey[m.key] ?? false)}
                              inputProps={{ inputMode: "numeric" }}
                              InputProps={{ readOnly: true }}
                              disabled
                              sx={{ "& .MuiInputBase-input": { color: bobotMeta?.activeByKey[m.key] ? "text.primary" : "text.disabled" } }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            <TextField
                              size="small"
                              value={row.months[m.key]?.forecast ?? ""}
                              onChange={(e) => handleDetailMonthChange(row.id, m.key, "forecast", e.target.value)}
                              inputProps={{ inputMode: "numeric" }}
                            />
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell sx={{ minWidth: 120 }}>
                        <TextField
                          size="small"
                          value={row.rencanaBisnis ?? ""}
                          onChange={(e) => handleDetailFieldChange(row.id, "rencanaBisnis", e.target.value)}
                          inputProps={{ inputMode: "numeric" }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <TextField
                          size="small"
                          value={row.realisasi ?? ""}
                          onChange={(e) => handleDetailFieldChange(row.id, "realisasi", e.target.value)}
                          inputProps={{ inputMode: "numeric" }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleRemoveDetailRow(row.id)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Untuk Biaya Konsinyasi sama tabel PBK. Ketika pendapatan dalam satu tabel saja.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Value tabel RKAP, Bobot &amp; Forecast: kosongkan kolom/angka yang tidak terisi.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Button onClick={handleCloseAddDialog} disabled={submitLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSaveDetails}
              disabled={submitLoading || rfTable.length === 0}
              sx={{ minWidth: 140, px: 4 }}
            >
              {submitLoading ? "Saving..." : "Save Details"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RollingForecast;
